import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
  numberAttribute,
} from "@angular/core";
import { PrimeTemplate } from "@bfx/primeng/api";
import { DomHandler } from "@bfx/primeng/dom";

/**
 * Custom panel resize start event.
 * @see {@link Splitter.onResizeStart}
 * @group Events
 */
export interface SplitterResizeStartEvent {
  /**
   * Browser event.
   */
  originalEvent: TouchEvent | MouseEvent;
  /**
   * Collapsed state of the panel.
   */
  sizes: number[];
}
/**
 * Custom panel resize end event.
 * @see {@link Splitter.onResizeEnd}
 * @extends {SplitterResizeStartEvent}
 * @group Events
 */
export interface SplitterResizeEndEvent extends SplitterResizeStartEvent {}

type Nullable<T = void> = T | null | undefined;
type VoidListener = VoidFunction | null | undefined;

/**
 * Splitter is utilized to separate and resize panels. This is basically a full copy of the PrimeNG splitter component
 * but modified to use the Ion L&F and supports maximising capability. We already had styles that added Ion DS tokens
 * to the p-splitter classes so we have retained those splitter classes here.
 *
 * To use, there is no need to worry about splitter styles, there are embedded in this class.
 * A simple usage looks like:
 * @example
 * <bfx-splitter #splitter layout="vertical" [minSizesPx] = "[60, 60]">
 *   <ng-template pTemplate>... </ng-template pTemplate>
 *   <ng-template pTemplate>... </ng-template pTemplate>
 * </bfx-splitter>
 *
 * 'Whatever happened to the Popular Front, Reg?'
 * 'He's over there.'
 * 'SPLITTER!'
 */

@Component({
  selector: "bfx-splitter",
  templateUrl: "./html/bfx-splitter.html",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "bfx-splitter-container",
  },
  standalone: false,
})
export class BfxSplitterComponent {
  /** Style class of the component */
  @Input() styleClass: string | undefined;

  /** Style class of the panel. */
  @Input() panelStyleClass: string | undefined;

  /** Inline style of the component. */
  @Input() style: { [klass: string]: any } | null | undefined;

  /** Inline style of the panel. */
  @Input() panelStyle: { [klass: string]: any } | null | undefined;

  /** Orientation of the panels. Valid values are 'horizontal' and 'vertical'. */
  @Input() layout: string | undefined = "horizontal";

  /** Size of the divider in pixels.*/
  @Input({ transform: numberAttribute }) gutterSize: number = 4;

  /** Step factor to increment/decrement the size of the panels while pressing the arrow keys. */
  @Input({ transform: numberAttribute }) step: number = 5;

  /** Minimum size of the elements relative to 100%. */
  @Input() minSizes: number[] = [];

  /** Minimum size of the elements in pixels */
  @Input() minSizesPx: number[] = [];

  @Input({ transform: numberAttribute }) maxPanelIndex: number = -1;

  /** Promise resolved when component is ready */
  public initialized: Promise<void>;

  /** Called to resolve this initialise promise */
  private initResolveFn: () => void;

  /** Size of the elements relative to 100%. */
  @Input() get panelSizes(): number[] {
    return this._panelSizes;
  }

  set panelSizes(val: number[]) {
    this.setPanelSizes(val);
  }

  /**
   * Callback to invoke when resize ends.
   * @param {SplitterResizeEndEvent} event - Custom panel resize end event
   */
  @Output() onResizeEnd: EventEmitter<SplitterResizeEndEvent> =
    new EventEmitter<SplitterResizeEndEvent>();
  /**
   * Callback to invoke when resize starts.
   * @param {SplitterResizeStartEvent} event - Custom panel resize start event
   */
  @Output() onResizeStart: EventEmitter<SplitterResizeStartEvent> =
    new EventEmitter<SplitterResizeStartEvent>();

  @ContentChildren(PrimeTemplate) templates!: QueryList<PrimeTemplate>;

  @ViewChild("container", { static: false }) containerViewChild: Nullable<ElementRef>;

  nested: boolean = false;

  panels: any[] = [];

  dragging: boolean = false;

  mouseMoveListener: VoidListener;

  mouseUpListener: VoidListener;

  touchMoveListener: VoidListener;

  touchEndListener: VoidListener;

  size: Nullable<number>;

  gutterElement: Nullable<ElementRef | HTMLElement>;

  startPos: Nullable<number>;

  prevPanelElement: Nullable<ElementRef | HTMLElement>;

  nextPanelElement: Nullable<ElementRef | HTMLElement>;

  nextPanelSize: Nullable<number>;

  prevPanelSize: Nullable<number>;

  _panelSizes: number[] = [];

  prevPanelIndex: Nullable<number>;

  timer: any;

  prevSize: any;

  // private window: Window;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private renderer: Renderer2,
    public cd: ChangeDetectorRef,
    private el: ElementRef,
  ) {
    // this.window = this.document.defaultView as Window;
    this.initialized = new Promise((resolve) => (this.initResolveFn = resolve));
  }

  ngOnInit() {
    this.nested = this.isNested();
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   console.log(changes.toString());
  // }

  ngAfterContentInit() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case "panel":
          this.panels.push(item.template);
          break;
        default:
          this.panels.push(item.template);
          break;
      }
    });
  }

  ngAfterViewInit() {
    if (
      !this._panelSizes ||
      (Array.isArray(this._panelSizes) && this._panelSizes.length !== this.panels?.length)
    ) {
      // As this is an input, it can be set to an empty value so make sure we recover
      this._panelSizes = [];
    }
    if (isPlatformBrowser(this.platformId)) {
      if (this.panels && this.panels.length) {
        let initialized = false;

        if (!initialized) {
          let children = [...this.el.nativeElement.children[0].children].filter((child) =>
            DomHandler.hasClass(child, "p-splitter-panel"),
          );
          let _panelSizes = [];

          this.panels.map((panel, i) => {
            let panelInitialSize = this.panelSizes.length - 1 >= i ? this.panelSizes[i] : null;
            let panelSize = panelInitialSize || 100 / this.panels.length;
            _panelSizes[i] = panelSize;
            children[i].style.flexBasis =
              "calc(" +
              panelSize +
              "% - " +
              (this.panels.length - 1) * (this.gutterSize as number) +
              "px)";
          });

          this._panelSizes = _panelSizes;

          this.prevSize = parseFloat(_panelSizes[0]).toFixed(4);
        }
      }
    }
    this.initResolveFn();
  }

  resizeStart(event: TouchEvent | MouseEvent, index: number, isKeyDown?: boolean) {
    this.gutterElement =
      (event.currentTarget as HTMLElement) || (event.target as HTMLElement).parentElement;
    this.size = this.horizontal()
      ? DomHandler.getWidth((this.containerViewChild as ElementRef).nativeElement)
      : DomHandler.getHeight((this.containerViewChild as ElementRef).nativeElement);

    if (!isKeyDown) {
      this.dragging = true;
      const isMouse = event instanceof MouseEvent || "pageX" in event;
      this.startPos = this.horizontal()
        ? isMouse
          ? (event as MouseEvent).pageX
          : event.changedTouches[0].pageX
        : isMouse
          ? (event as MouseEvent).pageY
          : event.changedTouches[0].pageY;
    }

    this.prevPanelElement = this.gutterElement.previousElementSibling as HTMLElement;
    this.nextPanelElement = this.gutterElement.nextElementSibling as HTMLElement;

    if (isKeyDown) {
      this.prevPanelSize = this.horizontal()
        ? DomHandler.getOuterWidth(this.prevPanelElement, true)
        : DomHandler.getOuterHeight(this.prevPanelElement, true);
      this.nextPanelSize = this.horizontal()
        ? DomHandler.getOuterWidth(this.nextPanelElement, true)
        : DomHandler.getOuterHeight(this.nextPanelElement, true);
    } else {
      this.prevPanelSize =
        (100 *
          (this.horizontal()
            ? DomHandler.getOuterWidth(this.prevPanelElement, true)
            : DomHandler.getOuterHeight(this.prevPanelElement, true))) /
        this.size;
      this.nextPanelSize =
        (100 *
          (this.horizontal()
            ? DomHandler.getOuterWidth(this.nextPanelElement, true)
            : DomHandler.getOuterHeight(this.nextPanelElement, true))) /
        this.size;
    }

    this.prevPanelIndex = index;
    DomHandler.addClass(this.gutterElement, "p-splitter-gutter-resizing");
    this.gutterElement.setAttribute("data-p-gutter-resizing", "true");
    DomHandler.addClass(
      (this.containerViewChild as ElementRef).nativeElement,
      "p-splitter-resizing",
    );
    this.containerViewChild.nativeElement.setAttribute("data-p-resizing", "true");
    this.onResizeStart.emit({ originalEvent: event, sizes: this._panelSizes as number[] });
  }

  onResize(event: MouseEvent, step?: number, isKeyDown?: boolean) {
    let newPos, newPrevPanelSize, newNextPanelSize;

    if (isKeyDown) {
      if (this.horizontal()) {
        newPrevPanelSize = (100 * (this.prevPanelSize + step)) / this.size;
        newNextPanelSize = (100 * (this.nextPanelSize - step)) / this.size;
      } else {
        newPrevPanelSize = (100 * (this.prevPanelSize - step)) / this.size;
        newNextPanelSize = (100 * (this.nextPanelSize + step)) / this.size;
      }
    } else {
      if (this.horizontal())
        newPos = (event.pageX * 100) / this.size - (this.startPos * 100) / this.size;
      else newPos = (event.pageY * 100) / this.size - (this.startPos * 100) / this.size;

      newPrevPanelSize = (this.prevPanelSize as number) + newPos;
      newNextPanelSize = (this.nextPanelSize as number) - newPos;
    }

    this.prevSize = parseFloat(newPrevPanelSize).toFixed(4);

    if (this.validateResize(newPrevPanelSize, newNextPanelSize)) {
      (this.prevPanelElement as HTMLElement).style.flexBasis = `${newPrevPanelSize}%`;
      (this.nextPanelElement as HTMLElement).style.flexBasis = `${newNextPanelSize}%`;
      this._panelSizes[this.prevPanelIndex as number] = newPrevPanelSize;
      this._panelSizes[(this.prevPanelIndex as number) + 1] = newNextPanelSize;
    }
  }

  resizeEnd(event: MouseEvent | TouchEvent) {
    this.onResizeEnd.emit({ originalEvent: event, sizes: this._panelSizes });
    DomHandler.removeClass(this.gutterElement, "p-splitter-gutter-resizing");
    DomHandler.removeClass(
      (this.containerViewChild as ElementRef).nativeElement,
      "p-splitter-resizing",
    );
    this.clear();
  }

  onGutterMouseDown(event: MouseEvent, index: number) {
    this.resizeStart(event, index);
    this.bindMouseListeners();
  }

  onGutterTouchStart(event: TouchEvent, index: number) {
    if (event.cancelable) {
      this.resizeStart(event, index);
      this.bindTouchListeners();

      event.preventDefault();
    }
  }

  onGutterTouchMove(event) {
    this.onResize(event);
    event.preventDefault();
  }

  onGutterTouchEnd(event: TouchEvent) {
    this.resizeEnd(event);
    this.unbindTouchListeners();

    if (event.cancelable) event.preventDefault();
  }

  repeat(event, index, step) {
    this.resizeStart(event, index, true);
    this.onResize(event, step, true);
  }

  setTimer(event, index, step) {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.repeat(event, index, step);
    }, 40);
  }

  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  onGutterKeyUp(event) {
    this.clearTimer();
    this.resizeEnd(event);
  }

  onGutterKeyDown(event, index) {
    switch (event.code) {
      case "ArrowLeft": {
        if (this.layout === "horizontal") {
          this.setTimer(event, index, this.step * -1);
        }

        event.preventDefault();
        break;
      }

      case "ArrowRight": {
        if (this.layout === "horizontal") {
          this.setTimer(event, index, this.step);
        }

        event.preventDefault();
        break;
      }

      case "ArrowDown": {
        if (this.layout === "vertical") {
          this.setTimer(event, index, this.step * -1);
        }

        event.preventDefault();
        break;
      }

      case "ArrowUp": {
        if (this.layout === "vertical") {
          this.setTimer(event, index, this.step);
        }

        event.preventDefault();
        break;
      }

      default:
        //no op
        break;
    }
  }

  validateResize(newPrevPanelSize: number, newNextPanelSize: number) {
    if (this.minSizes.length > 0) {
      // Check relative percentage sizes against defined constraints
      if (this.minSizes[0] && this.minSizes[0] > newPrevPanelSize) {
        return false;
      }
      if (this.minSizes.length > 1 && this.minSizes[1] && this.minSizes[1] > newNextPanelSize) {
        return false;
      }
    }
    if (this.minSizesPx.length > 0) {
      const onePercent = this.size / 100;
      // Check the absolute panel sizes against size constraints
      if (this.minSizesPx[0] && this.minSizesPx[0] > newPrevPanelSize * onePercent) {
        return false;
      }
      if (
        this.minSizesPx.length > 1 &&
        this.minSizesPx[1] &&
        this.minSizesPx[1] > newNextPanelSize * onePercent
      ) {
        return false;
      }
    }

    return true;
  }

  bindMouseListeners() {
    if (this.el?.nativeElement) {
      let document: Document = DomHandler.getDocument(this.el.nativeElement);
      if (!this.mouseMoveListener) {
        this.mouseMoveListener = this.renderer.listen(document, "mousemove", (event) => {
          this.onResize(event);
        });
      }

      if (!this.mouseUpListener) {
        this.mouseUpListener = this.renderer.listen(document, "mouseup", (event) => {
          this.resizeEnd(event);
          this.unbindMouseListeners();
        });
      }
    }
  }

  bindTouchListeners() {
    if (this.el?.nativeElement) {
      let document: Document = DomHandler.getDocument(this.el.nativeElement);
      if (!this.touchMoveListener) {
        this.touchMoveListener = this.renderer.listen(document, "touchmove", (event) => {
          this.onResize(event.changedTouches[0]);
        });
      }

      if (!this.touchEndListener) {
        this.touchEndListener = this.renderer.listen(document, "touchend", (event) => {
          this.resizeEnd(event);
          this.unbindTouchListeners();
        });
      }
    }
  }

  unbindMouseListeners() {
    if (this.mouseMoveListener) {
      this.mouseMoveListener();
      this.mouseMoveListener = null;
    }

    if (this.mouseUpListener) {
      this.mouseUpListener();
      this.mouseUpListener = null;
    }
  }

  unbindTouchListeners() {
    if (this.touchMoveListener) {
      this.touchMoveListener();
      this.touchMoveListener = null;
    }

    if (this.touchEndListener) {
      this.touchEndListener();
      this.touchEndListener = null;
    }
  }

  clear() {
    this.dragging = false;
    this.size = null;
    this.startPos = null;
    this.prevPanelElement = null;
    this.nextPanelElement = null;
    this.prevPanelSize = null;
    this.nextPanelSize = null;
    this.gutterElement = null;
    this.prevPanelIndex = null;
  }

  isNested() {
    if (this.el.nativeElement) {
      let parent = this.el.nativeElement.parentElement;
      while (parent && !DomHandler.hasClass(parent, "p-splitter")) {
        parent = parent.parentElement;
      }

      return parent !== null;
    } else {
      return false;
    }
  }

  /**
   * Set the relative sizes of each panel and storr in local _panelSizes property
   * @param val
   */
  private setPanelSizes(val: number[]) {
    this._panelSizes = val;

    if (this.el && this.el.nativeElement && this.panels.length > 0) {
      let children = [...this.el.nativeElement.children[0].children].filter((child) =>
        DomHandler.hasClass(child, "p-splitter-panel"),
      );
      let _panelSizes = [];

      this.panels.map((panel, i) => {
        let panelInitialSize = this.panelSizes.length - 1 >= i ? this.panelSizes[i] : null;
        let panelSize = panelInitialSize || 100 / this.panels.length;
        _panelSizes[i] = panelSize;
        children[i].style.flexBasis = `${panelSize}%`;
      });
    }
  }

  containerClass() {
    return {
      "p-splitter p-component": true,
      "p-splitter-horizontal": this.layout === "horizontal",
      "p-splitter-vertical": this.layout === "vertical",
    };
  }

  panelContainerClass(index: number) {
    if (this.maxPanelIndex == -1 || this.maxPanelIndex == index) {
      return {
        "p-splitter-panel": true,
        "p-splitter-panel-nested": true,
      };
    }
    return {
      "hidden-splitter-panel": true,
    };
  }

  public splitterGutterClass() {
    return this.layout === "horizontal"
      ? "bfx-splitter-gutter-bold-horizontal"
      : "bfx-splitter-gutter-bold-vertical";
  }

  horizontal() {
    return this.layout === "horizontal";
  }

  maximizePanel(panelIndex: number) {
    this.initialized.then(() => {
      if (panelIndex >= -1 && panelIndex < this.panels.length) {
        this.maxPanelIndex = panelIndex;
        this.cd.markForCheck();
      }
    });
  }
  restore() {
    this.maximizePanel(-1);
  }
}
